import React, { useState } from 'react';
import { Card, Radio, Button, Space, Typography, Divider, Tooltip, Badge } from 'antd';
import { 
    CreditCardOutlined, 
    MobileOutlined, 
    BankOutlined, 
    WalletOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { usePayment } from '../../contexts/PaymentContext';

const { Title, Text, Paragraph } = Typography;

const PaymentMethodSelector = ({ 
    amount, 
    onPaymentMethodChange, 
    selectedMethod, 
    disabled = false 
}) => {
    const { 
        paymentMethods, 
        setPaymentMethod, 
        getSelectedPaymentMethod,
        getPaymentMethodIcon,
        getPaymentMethodDescription 
    } = usePayment();

    const handleMethodChange = (e) => {
        const methodId = e.target.value;
        setPaymentMethod(methodId);
        onPaymentMethodChange?.(methodId);
    };

    const getMethodIcon = (methodId) => {
        const iconMap = {
            razorpay: <CreditCardOutlined />,
            stripe: <CreditCardOutlined />,
            cod: <MobileOutlined />,
            upi: <MobileOutlined />,
            netbanking: <BankOutlined />,
            wallet: <WalletOutlined />
        };
        return iconMap[methodId] || <CreditCardOutlined />;
    };

    const getMethodFeatures = (methodId) => {
        const features = {
            razorpay: ['UPI', 'Cards', 'Wallets', 'Net Banking'],
            stripe: ['Cards', 'Apple Pay', 'Google Pay'],
            cod: ['Cash on Delivery']
        };
        return features[methodId] || [];
    };

    const getMethodInfo = (methodId) => {
        const info = {
            razorpay: 'Secure payment gateway supporting all major payment methods in India',
            stripe: 'International payment gateway with advanced fraud protection',
            cod: 'Pay when your order is delivered. Additional charges may apply.'
        };
        return info[methodId] || '';
    };

    const isRecommended = (methodId) => {
        return methodId === 'razorpay'; // Razorpay is recommended for Indian market
    };

    const hasProcessingFee = (methodId) => {
        return methodId === 'cod'; // COD might have processing fees
    };

    const getProcessingFee = (methodId) => {
        if (methodId === 'cod') {
            return Math.min(50, amount * 0.02); // 2% or max ₹50
        }
        return 0;
    };

    const totalAmount = amount + getProcessingFee(selectedMethod);

    return (
        <div className="space-y-4">
            <div>
                <Title level={4} className="mb-2">Choose Payment Method</Title>
                <Text type="secondary">
                    Select your preferred payment method to complete the order
                </Text>
            </div>

            <Radio.Group 
                value={selectedMethod} 
                onChange={handleMethodChange}
                className="w-full"
                disabled={disabled}
            >
                <Space direction="vertical" className="w-full" size="middle">
                    {paymentMethods.map((method) => (
                        <Card
                            key={method.id}
                            className={`payment-method-card ${
                                selectedMethod === method.id ? 'selected' : ''
                            } ${isRecommended(method.id) ? 'recommended' : ''}`}
                            hoverable={!disabled}
                        >
                            <Radio value={method.id} className="w-full">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <div className="text-2xl">
                                            {getMethodIcon(method.id)}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <Text strong className="text-base">
                                                    {method.name}
                                                </Text>
                                                {isRecommended(method.id) && (
                                                    <Badge 
                                                        count="Recommended" 
                                                        style={{ 
                                                            backgroundColor: '#52c41a',
                                                            fontSize: '10px',
                                                            height: '18px',
                                                            lineHeight: '18px'
                                                        }} 
                                                    />
                                                )}
                                            </div>
                                            <Text type="secondary" className="text-sm">
                                                {getPaymentMethodDescription(method.id)}
                                            </Text>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        {method.id === selectedMethod && (
                                            <CheckCircleOutlined className="text-blue-500" />
                                        )}
                                        <Tooltip title={getMethodInfo(method.id)}>
                                            <InfoCircleOutlined className="text-gray-400" />
                                        </Tooltip>
                                    </div>
                                </div>
                            </Radio>

                            {/* Payment method features */}
                            <div className="mt-3 ml-8">
                                <div className="flex flex-wrap gap-1">
                                    {getMethodFeatures(method.id).map((feature) => (
                                        <span
                                            key={feature}
                                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs"
                                        >
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    ))}
                </Space>
            </Radio.Group>

            {/* Payment summary */}
            {selectedMethod && (
                <Card className="payment-summary">
                    <Title level={5} className="mb-3">Payment Summary</Title>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <Text>Order Amount:</Text>
                            <Text>₹{amount.toLocaleString()}</Text>
                        </div>
                        
                        {hasProcessingFee(selectedMethod) && (
                            <div className="flex justify-between">
                                <Text>Processing Fee:</Text>
                                <Text>₹{getProcessingFee(selectedMethod).toLocaleString()}</Text>
                            </div>
                        )}
                        
                        <Divider className="my-2" />
                        
                        <div className="flex justify-between">
                            <Text strong className="text-lg">Total Amount:</Text>
                            <Text strong className="text-lg text-blue-600">
                                ₹{totalAmount.toLocaleString()}
                            </Text>
                        </div>
                    </div>

                    {hasProcessingFee(selectedMethod) && (
                        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                            <Text type="secondary" className="text-xs">
                                <InfoCircleOutlined className="mr-1" />
                                Processing fee applies for this payment method
                            </Text>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default PaymentMethodSelector;
